import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const CardContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  background: #ffffff;
  border-radius: 20px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.02);
  margin-bottom: 0.75rem;
  width: 100%;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  }

  @media (max-width: 600px) {
    padding: 0.75rem 1rem;
    gap: 0.75rem;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;

  @media (max-width: 600px) {
    gap: 0.75rem;
  }
`;

const TimeBadge = styled.div`
  background: #f3f4f6;
  color: #111827;
  padding: 0.6rem 1rem;
  border-radius: 12px;
  font-weight: 800;
  font-size: var(--font-size-p);
  min-width: 90px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 600px) {
    min-width: 75px;
    padding: 0.5rem 0.75rem;
    font-size: var(--font-size-small);
  }
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const DateText = styled.div`
  font-size: var(--font-size-h6);
  font-weight: 800;
  color: var(--text-main);

  @media (max-width: 600px) {
    font-size: var(--font-size-p);
  }
`;

const SubtitleText = styled.div`
  font-size: var(--font-size-small);
  color: #6b7280;
  font-weight: 500;

  @media (max-width: 600px) {
    font-size: var(--font-size-xs);
  }
`;

const BookButton = styled.button`
  background: var(--color-coral, #FF6648);
  color: white;
  padding: 0.6rem 1.75rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: var(--font-size-small);
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(255, 102, 72, 0.2);

  &:hover {
    background: #e55b40;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(255, 102, 72, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }

  @media (max-width: 600px) {
    padding: 0.5rem 1.25rem;
    font-size: var(--font-size-xs);
  }
`;

interface LessonCardProps {
  lesson: {
    id: string | number;
    dateTime: string;
    duration: number;
    location?: string;
    pickupLocation?: string;
    status?: string;
    cost?: number;
    currency?: string;
    type?: string;
    studentName?: string;
  };
  mode?: 'available' | 'booked' | 'passed';
  onBook?: (id: string | number) => void | Promise<void>;
  isBooking?: boolean;
  onClick?: (id: string | number) => void;
  showPrice?: boolean;
}

const LessonCard: React.FC<LessonCardProps> = ({ lesson, mode = 'available', onBook, isBooking, onClick, showPrice }) => {
  const { t, i18n } = useTranslation();

  const dt = new Date(lesson.dateTime);
  
  // Format Time (e.g., 01:00 PM)
  const timeStr = dt.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true 
  }).toUpperCase();

  // Format Date (e.g., 23 Dec 2025)
  const dateStr = dt.toLocaleDateString(i18n.language === 'nl' ? 'nl-NL' : 'en-GB', { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric' 
  });

  const getStatusLabel = () => {
    if (mode === 'passed') return t('portal.lesson.status.completed', 'Completed');
    if (mode === 'booked') return t('portal.lesson.status.scheduled', 'Scheduled');
    return null;
  };

  const statusLabel = getStatusLabel();

  const formatCurrency = (amount?: number, currency: string = 'EUR') => {
    if (amount === undefined || amount === null) return '';
    return new Intl.NumberFormat(i18n.language === 'nl' ? 'nl-NL' : 'en-GB', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const isExam = lesson.type?.toUpperCase() === 'EXAM';

  return (
    <CardContainer onClick={() => onClick?.(lesson.id)} style={{ cursor: onClick ? 'pointer' : 'default' }}>
      <LeftSection>
        <TimeBadge>{timeStr}</TimeBadge>
        <InfoSection>
          <DateText>
            {isExam && <span style={{ color: 'var(--color-vibrant-blue)', marginRight: '0.5rem', fontWeight: 700 }}>EXAM</span>}
            {dateStr}
          </DateText>
          <SubtitleText>
            {lesson.studentName && <span style={{ fontWeight: 800, color: '#111827', marginRight: '0.4rem' }}>{lesson.studentName} •</span>}
            {lesson.duration} {t('portal.student.dashboard.minutes_short', 'min')} • {lesson.pickupLocation || lesson.location || '—'}
          </SubtitleText>
        </InfoSection>
      </LeftSection>

      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        {showPrice && lesson.cost !== undefined && (
          <div style={{ fontWeight: 900, fontSize: '1.1rem', color: '#111827' }}>
            {formatCurrency(lesson.cost, lesson.currency)}
          </div>
        )}

        {mode === 'available' && onBook && (
          <BookButton 
            onClick={(e) => {
              e.stopPropagation();
              onBook(lesson.id);
            }} 
            disabled={isBooking}
          >
            {isBooking ? '...' : t('portal.common.book', 'Book')}
          </BookButton>
        )}

        {statusLabel && (
          <div style={{ 
            fontSize: '0.85rem', 
            fontWeight: 700, 
            color: mode === 'passed' ? '#6b7280' : 'var(--color-vibrant-blue)',
            textTransform: 'uppercase',
            padding: '0.5rem 1rem'
          }}>
            {statusLabel}
          </div>
        )}
      </div>
    </CardContainer>
  );
};

export default LessonCard;
